/**
 * Investigation entity — wraps the canonical `GraphView` with metadata.
 *
 * An Investigation is a named, durable, mutable graph view of a case, with
 * author, notes, and a read-only flag. Its inner data is the canonical
 * `GraphView` from `./graph-view`.
 *
 * PARTNER FILE — `src/libs/services/spectre/services/case_investigations.py`.
 * The Pydantic definitions there must stay structurally identical to these
 * types. Any change here MUST land in the same review as the matching change
 * there; otherwise producer drift breaks consumers at runtime.
 */

import type { GraphView } from './graph-view'

export type InvestigationSource = 'user' | 'ai'
export type InvestigationConfidence = 'supported' | 'refuted' | 'inconclusive'

/** What a note is (PRD-0075 R-E4): an assessment note logs `assessment_changed` and shows on
 *  the hypothesis its investigation is evidence for. */
export const INVESTIGATION_NOTE_KINDS = ['observation', 'assessment', 'task'] as const
export type InvestigationNoteKind = (typeof INVESTIGATION_NOTE_KINDS)[number]

export interface InvestigationNote {
  id: string
  text: string
  authorId?: string
  authorName: string
  /** ISO-8601 UTC */
  createdAt: string
  /** The server answers it always; a note written without one is an observation. */
  kind?: InvestigationNoteKind
}

/**
 * Agent citation — natural-language claim + source ref string. Mirrors a
 * research run's `sources[]` (and the legacy `structured_output.evidence[]`).
 * `source` is a ref like `evidence:<id>`, `node:<elementId>`, `doc:<id>` or
 * `doc:<id>#t=<seconds>`; null when it was dropped at save time (ADR-0097).
 * `label` is the source's name resolved when it was saved (document, entity,
 * snippet) — shown before any live lookup.
 */
export interface EvidenceCitation {
  text: string
  source?: string | null
  label?: string | null
}

export interface Investigation {
  id: string
  caseId: string
  title: string
  source: InvestigationSource
  authorId?: string
  authorName: string
  readOnly: boolean
  graphView: GraphView
  notes: InvestigationNote[]
  includedInReport: boolean
  /** ISO-8601 UTC */
  reportAddedAt: string | null
  /** Link to the parent hypothesis when promoted from one. */
  hypothesisId?: string | null
  /** Research-snapshot: executive narrative (markdown). */
  summary?: string | null
  /** Research-snapshot: verdict. */
  confidence?: InvestigationConfidence | null
  /** Research-snapshot: verdict reasoning text. */
  confidenceText?: string | null
  /** Research-snapshot: agent citations. */
  evidence?: EvidenceCitation[] | null
  /** Research-snapshot: the HypothesisRun this snapshot was taken from. */
  sourceRunId?: string | null
  /** The full narrative of a saved research report (ADR-0097). Set only on a
   *  report investigation; `summary` stays its excerpt. */
  reportMd?: string | null
  /** The hypothesis (`analytic_hypotheses`, ADR-0095) this investigation is evidence for. */
  hypothesisRef?: string | null
  /** Precomputed map projection ({anchors, tracks, events}) of this
   *  investigation's object set, recomputed server-side on save + sync. The
   *  map renders from this instead of calling the live /geo endpoint. Null
   *  until first computed. */
  geo?: InvestigationGeo | null
  /** ISO-8601 UTC */
  createdAt: string
  /** ISO-8601 UTC */
  updatedAt: string
}

/** Body for POST /v1/cases/:caseId/investigations. */
export interface InvestigationCreateBody {
  title: string
  source: InvestigationSource
  graphView: GraphView
  authorId?: string
  authorName?: string
}

/** Body for PATCH /v1/cases/:caseId/investigations/:id (any subset). */
export interface InvestigationPatchBody {
  title?: string
  graphView?: GraphView
  notes?: InvestigationNote[]
  readOnly?: boolean
  includedInReport?: boolean
}

// --- Investigation geo projection (GET .../investigations/:id/geo?mode=) ---

export type InvestigationGeoMode = 'static' | 'animated' | 'time' | 'all'

export interface GeoAnchor {
  anchor_id: string
  name: string
  type?: string | null
  latitude: number
  longitude: number
  /** True for a bare Location no in-set entity is attached to — render dimmed. */
  dimmed?: boolean
  /** elementId of the Location this pin is placed at (via-edge), else null. */
  via_anchor_id?: string | null
  /** True for a non-trackable entity with timestamped geo observations —
   *  render as a time-anchored pin (filtered by the scrubber). */
  temporal?: boolean
  /** Temporal extent (ISO) of a time-anchored pin, for the timeline axis. */
  temporal_min?: string | null
  temporal_max?: string | null
}

export interface GeoTrackPoint {
  latitude: number
  longitude: number
  timestamp: string
  location_name?: string | null
}

export interface GeoTrack {
  entity_id: string
  name: string
  type?: string | null
  points: GeoTrackPoint[]
}

export interface GeoTimeEvent {
  entity_id: string
  entity_name: string
  type?: string | null
  latitude: number
  longitude: number
  timestamp: string
  location_name?: string | null
}

/** Response of GET .../investigations/:id/geo. Exactly one list is populated. */
export interface InvestigationGeo {
  mode: InvestigationGeoMode
  anchors?: GeoAnchor[] | null
  tracks?: GeoTrack[] | null
  events?: GeoTimeEvent[] | null
}
